import React, { ChangeEvent, ComponentProps, FC, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import Typography from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/typography';
import type { TypographyProps } from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/typography/types';

export interface DropdownProps extends ComponentProps<'select'> {
  className?: string;
  containerClassName?: string;
  labelClassName?: string;
  labelFontFamily?: TypographyProps['fontFamily'];
  items: Array<{ label: string; value: string }>;
  label?: string;
  value?: string;
  defaultValue?: string;
}

const DefaultDropdown: FC<DropdownProps> = ({
  className = '',
  containerClassName = '',
  labelClassName,
  labelFontFamily = 'primaryBold',
  label,
  items,
  onChange,
  value,
  ...props
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedValue(value);
    onChange?.(e);
  };

  const containerClassNames = useClassNames(['realtive mb-8', containerClassName]);

  const selectClassName = useClassNames([
    'absolute font-body z-[1] h-40 text-14 border-none focus:ring-0 w-full bg-site bg-none font-regular leading-loose pl-12 pr-0 py-0',
    className,
  ]);

  return (
    <>
      <div className={containerClassNames}>
        {label && (
          <Typography fontFamily={labelFontFamily} as="label" medium className={labelClassName}>
            {`${label}${props.required ? ' *' : ''}`}
          </Typography>
        )}
      </div>
      <div className="relative h-40 min-w-64 overflow-hidden rounded-lg border border-neutral-500 bg-site">
        <select
          className={selectClassName}
          value={value || selectedValue}
          onChange={handleChange}
          {...props}
          data-testid={`dropdown-${props.name || 'default'}`}
        >
          {items.map(({ label, value }, index) => (
            <option key={index} value={value}>
              {label}
            </option>
          ))}
        </select>

        <ChevronDownIcon className="absolute right-5 top-[50%] z-0 h-20 w-30 translate-y-[-50%] stroke-1 text-primary-regular" />
      </div>
    </>
  );
};

export default DefaultDropdown;
